/**
 * The label this prototype runs on: payees to add to a contract and a small
 * catalogue to pick its tracks from.
 *
 * Scaffolding — delete with the rest of src/prototypes/modular-contract-builder/.
 */
import { DEV_LABEL } from "@/lib/dev-mode";
import type { ContractExtras, SavedSubContract } from "./store";

const now = "2026-01-15T10:00:00Z";

export const LABEL = { ...DEV_LABEL, payment_threshold: "50.00" };

export const PAYEES = [
  {
    id: "payee-nova-skye",
    name: "Nova Skye",
    legal_name: "Skyeline Music Ltd",
    country: "GB",
    preferred_currency: "USD",
  },
  {
    id: "payee-marcus-vale",
    name: "Marcus Vale",
    legal_name: "Vale Recordings LLC",
    country: "US",
    preferred_currency: "USD",
  },
  {
    id: "payee-hollow-pines",
    name: "The Hollow Pines",
    legal_name: "Hollow Pines Collective",
    country: "CA",
    preferred_currency: "USD",
  },
  {
    id: "payee-june-ashworth",
    name: "June Ashworth",
    legal_name: "J. Ashworth Productions",
    country: "GB",
    preferred_currency: "GBP",
  },
  // A long name, to see how the split tables take it.
  {
    id: "payee-aurelia-montgomery",
    name: "Aurelia Montgomery-Fitzgerald & The Midnight Harbour Orchestra",
    legal_name: "Montgomery-Fitzgerald Music Publishing International Ltd",
    country: "IE",
    preferred_currency: "EUR",
  },
  // Five more, for the demo contract with ten payees.
  {
    id: "payee-elio-marchetti",
    name: "Elio Marchetti",
    legal_name: "Marchetti Sound S.r.l.",
    country: "IT",
    preferred_currency: "EUR",
  },
  {
    id: "payee-saffron-reed",
    name: "Saffron Reed",
    legal_name: "Reed House Music Ltd",
    country: "GB",
    preferred_currency: "GBP",
  },
  {
    id: "payee-kai-nakamura",
    name: "Kai Nakamura",
    legal_name: "Nakamura Works LLC",
    country: "US",
    preferred_currency: "USD",
  },
  {
    id: "payee-lumen-collective",
    name: "Lumen Collective",
    legal_name: "Lumen Collective AB",
    country: "SE",
    preferred_currency: "EUR",
  },
  {
    id: "payee-odessa-grey",
    name: "Odessa Grey",
    legal_name: "Grey Lantern Publishing Pty Ltd",
    country: "AU",
    preferred_currency: "USD",
  },
].map((payee) => ({
  ...payee,
  contact_email: `${payee.name.toLowerCase().replace(/[^a-z]+/g, ".")}@example.com`,
  city: "",
  is_active: true,
  label: LABEL.id,
  label_name: LABEL.name,
  created_at: now,
  updated_at: now,
  can_delete: false,
}));

/** A sample release's made-up cover, in public/artworks: not the real one, so artwork shows. */
export const releaseArtwork = (releaseId: string) => `/artworks/${releaseId}.svg`;

export const RELEASES = [
  {
    id: "rel-neon-harbour",
    title: "Neon Harbour",
    type: "album",
    release_date: "2025-03-14",
    catalog_number: "TRK012",
    upc_code: "196925401127",
  },
  {
    id: "rel-paper-lanterns",
    title: "Paper Lanterns",
    type: "ep",
    release_date: "2025-06-06",
    catalog_number: "TRK015",
    upc_code: "196925401158",
  },
  {
    id: "rel-slow-tide",
    title: "Slow Tide",
    type: "single",
    release_date: "2025-09-12",
    catalog_number: "TRK018",
    upc_code: "196925401189",
  },
  // A compilation: tracks first out on other releases, each now on two.
  {
    id: "rel-late-night-selections",
    title: "Late Night Selections",
    type: "compilation",
    release_date: "2025-11-07",
    catalog_number: "TRK020",
    upc_code: "196925401202",
    artist: "Various Artists",
  },
  // Not on any contract yet: the uploaded contract's tracks, free to pick.
  {
    id: "rel-afterglow",
    title: "Afterglow",
    type: "album",
    release_date: "2026-02-20",
    catalog_number: "TRK022",
    upc_code: "196925401226",
  },
  // Demo releases, on no contract: more tracks to pick from.
  {
    id: "rel-copper-sky",
    title: "Copper Sky",
    type: "ep",
    release_date: "2026-04-10",
    catalog_number: "TRK024",
    upc_code: "196925401240",
  },
  {
    id: "rel-northern-lines",
    title: "Northern Lines",
    type: "album",
    release_date: "2026-05-22",
    catalog_number: "TRK026",
    upc_code: "196925401264",
  },
  // The linking flow's release: two of its tracks on the Tidal Rooms deal, three not yet.
  {
    id: "rel-tidal-rooms",
    title: "Tidal Rooms",
    type: "ep",
    release_date: "2026-06-12",
    catalog_number: "TRK030",
    upc_code: "196925401301",
  }, // Demo contract 1's release: eight tracks, all on the contract.
  {
    id: "rel-night-garden",
    title: "Night Garden",
    type: "album",
    release_date: "2026-08-21",
    catalog_number: "TRK032",
    upc_code: "196925401325",
  },
  // The blocking cases' release: four tracks on each demo contract.
  {
    id: "rel-paper-harbour",
    title: "Paper Harbour",
    type: "album",
    release_date: "2025-10-03",
    catalog_number: "TRK034",
    upc_code: "196925401349",
  },
];

export const RECORDINGS = [
  {
    id: "rec-midnight-signal",
    title: "Midnight Signal",
    mix_name: "Original Mix",
    isrc: "GBTRK2500001",
    release: "rel-neon-harbour",
    track_number: "1",
    artist: "Nova Skye",
  },
  {
    id: "rec-harbour-lights",
    title: "Harbour Lights",
    mix_name: "Album Version",
    isrc: "GBTRK2500002",
    release: "rel-neon-harbour",
    // Also on the compilation: a track on two releases.
    also_on: [{ release: "rel-late-night-selections", track_number: "1" }],
    track_number: "2",
    artist: "Nova Skye",
  },
  {
    id: "rec-glass-avenue",
    title: "Glass Avenue",
    mix_name: "Radio Edit",
    isrc: "GBTRK2500003",
    release: "rel-neon-harbour",
    track_number: "3",
    artist: "Nova Skye",
  },
  {
    id: "rec-undertow",
    title: "Undertow",
    mix_name: "Club Mix",
    isrc: "GBTRK2500007",
    release: "rel-neon-harbour",
    track_number: "4",
    artist: "Nova Skye",
  },
  {
    id: "rec-salt-lines",
    title: "Salt Lines",
    mix_name: "Acoustic",
    isrc: "GBTRK2500008",
    release: "rel-paper-lanterns",
    track_number: "3",
    artist: "Marcus Vale",
  },
  {
    id: "rec-paper-lanterns",
    title: "Paper Lanterns",
    mix_name: "Single Version",
    isrc: "USTRK2500004",
    release: "rel-paper-lanterns",
    track_number: "1",
    artist: "Marcus Vale",
  },
  {
    id: "rec-northbound",
    title: "Northbound",
    mix_name: "Live",
    isrc: "USTRK2500005",
    release: "rel-paper-lanterns",
    also_on: [{ release: "rel-late-night-selections", track_number: "2" }],
    track_number: "2",
    artist: "Marcus Vale",
  },
  {
    id: "rec-slow-tide",
    title: "Slow Tide",
    mix_name: "Remastered",
    isrc: "CATRK2500006",
    release: "rel-slow-tide",
    track_number: "1",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-static-bloom",
    title: "Static Bloom",
    mix_name: "Instrumental",
    isrc: "GBTRK2600009",
    release: "rel-afterglow",
    track_number: "1",
    artist: "Nova Skye",
  },
  {
    id: "rec-low-orbit",
    title: "Low Orbit",
    mix_name: "Dub Mix",
    isrc: "GBTRK2600010",
    release: "rel-afterglow",
    track_number: "2",
    artist: "Nova Skye",
  },
  {
    id: "rec-coastline",
    title: "Coastline",
    mix_name: "Piano Version",
    isrc: "GBTRK2600011",
    release: "rel-afterglow",
    track_number: "3",
    artist: "Nova Skye",
  },
  {
    id: "rec-after-hours",
    title: "After Hours",
    mix_name: "Extended Mix",
    isrc: "GBTRK2600012",
    release: "rel-afterglow",
    track_number: "4",
    artist: "Nova Skye",
  },
  // The demo releases' tracks: Copper Sky's, then Northern Lines'.
  {
    id: "rec-copper-sky",
    title: "Copper Sky",
    mix_name: "Original Mix",
    isrc: "GBTRK2600013",
    release: "rel-copper-sky",
    track_number: "1",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-quiet-engines",
    title: "Quiet Engines",
    mix_name: "Radio Edit",
    isrc: "GBTRK2600014",
    release: "rel-copper-sky",
    track_number: "2",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-dust-and-chrome",
    title: "Dust and Chrome",
    mix_name: "Album Version",
    isrc: "GBTRK2600015",
    release: "rel-copper-sky",
    track_number: "3",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-signal-fire",
    title: "Signal Fire",
    mix_name: "Live Session",
    isrc: "GBTRK2600016",
    release: "rel-copper-sky",
    track_number: "4",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-long-way-home",
    title: "Long Way Home",
    mix_name: "Acoustic",
    isrc: "GBTRK2600017",
    release: "rel-copper-sky",
    track_number: "5",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-northern-lines",
    title: "Northern Lines",
    mix_name: "Original Mix",
    isrc: "GBTRK2600018",
    release: "rel-northern-lines",
    track_number: "1",
    artist: "June Ashworth",
  },
  {
    id: "rec-paper-planes",
    title: "Paper Planes",
    mix_name: "Single Edit",
    isrc: "GBTRK2600019",
    release: "rel-northern-lines",
    track_number: "2",
    artist: "June Ashworth",
  },
  {
    id: "rec-cold-light",
    title: "Cold Light",
    mix_name: "Album Version",
    isrc: "GBTRK2600020",
    release: "rel-northern-lines",
    track_number: "3",
    artist: "June Ashworth",
  },
  {
    id: "rec-harbour-wall",
    title: "Harbour Wall",
    mix_name: "Demo",
    isrc: "GBTRK2600021",
    release: "rel-northern-lines",
    track_number: "4",
    artist: "June Ashworth",
  },
  {
    id: "rec-last-ferry",
    title: "Last Ferry",
    mix_name: "Reprise",
    isrc: "GBTRK2600022",
    release: "rel-northern-lines",
    track_number: "5",
    artist: "June Ashworth",
  },
  // Tidal Rooms: two tracks on the linking flow's contract, the rest free to link.
  {
    id: "rec-tidal-rooms",
    title: "Tidal Rooms",
    mix_name: "Original Mix",
    isrc: "GBTRK2600030",
    release: "rel-tidal-rooms",
    track_number: "1",
    artist: "Marcus Vale",
  },
  {
    id: "rec-lantern-bay",
    title: "Lantern Bay",
    mix_name: "Radio Edit",
    isrc: "GBTRK2600031",
    release: "rel-tidal-rooms",
    track_number: "2",
    artist: "Marcus Vale",
  },
  {
    id: "rec-echo-street",
    title: "Echo Street",
    mix_name: "Original Mix",
    isrc: "GBTRK2600032",
    release: "rel-tidal-rooms",
    track_number: "3",
    artist: "Marcus Vale",
  },
  {
    id: "rec-silver-wake",
    title: "Silver Wake",
    mix_name: "Extended Mix",
    isrc: "GBTRK2600033",
    release: "rel-tidal-rooms",
    track_number: "4",
    artist: "Marcus Vale",
  },
  {
    id: "rec-morning-static",
    title: "Morning Static",
    mix_name: "Acoustic",
    isrc: "GBTRK2600034",
    release: "rel-tidal-rooms",
    track_number: "5",
    artist: "Marcus Vale",
  },
  // Night Garden: Demo contract 1's eight tracks.
  {
    id: "rec-moonlit-path",
    title: "Moonlit Path",
    mix_name: "Original Mix",
    isrc: "GBTRK2600040",
    release: "rel-night-garden",
    track_number: "1",
    artist: "Nova Skye",
  },
  {
    id: "rec-fern-hollow",
    title: "Fern Hollow",
    mix_name: "Radio Edit",
    isrc: "GBTRK2600041",
    release: "rel-night-garden",
    track_number: "2",
    artist: "Nova Skye",
  },
  {
    id: "rec-glasshouse",
    title: "Glasshouse",
    mix_name: "Original Mix",
    isrc: "GBTRK2600042",
    release: "rel-night-garden",
    track_number: "3",
    artist: "Nova Skye",
  },
  {
    id: "rec-night-bloom",
    title: "Night Bloom",
    mix_name: "Extended Mix",
    isrc: "GBTRK2600043",
    release: "rel-night-garden",
    track_number: "4",
    artist: "Nova Skye",
  },
  {
    id: "rec-quiet-orchard",
    title: "Quiet Orchard",
    mix_name: "Acoustic",
    isrc: "GBTRK2600044",
    release: "rel-night-garden",
    track_number: "5",
    artist: "Nova Skye",
  },
  {
    id: "rec-lantern-moth",
    title: "Lantern Moth",
    mix_name: "Original Mix",
    isrc: "GBTRK2600045",
    release: "rel-night-garden",
    track_number: "6",
    artist: "Nova Skye",
  },
  {
    id: "rec-ivy-steps",
    title: "Ivy Steps",
    mix_name: "Instrumental",
    isrc: "GBTRK2600046",
    release: "rel-night-garden",
    track_number: "7",
    artist: "Nova Skye",
  },
  {
    id: "rec-dawn-chorus",
    title: "Dawn Chorus",
    mix_name: "Reprise",
    isrc: "GBTRK2600047",
    release: "rel-night-garden",
    track_number: "8",
    artist: "Nova Skye",
  },
  // Paper Harbour: four tracks on each of the blocking cases' demo contracts.
  {
    id: "rec-harbour-lamps",
    title: "Harbour Lamps",
    mix_name: "Original Mix",
    isrc: "GBTRK2500060",
    release: "rel-paper-harbour",
    track_number: "1",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-salt-wind",
    title: "Salt Wind",
    mix_name: "Radio Edit",
    isrc: "GBTRK2500061",
    release: "rel-paper-harbour",
    track_number: "2",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-low-tide-waltz",
    title: "Low Tide Waltz",
    mix_name: "Original Mix",
    isrc: "GBTRK2500062",
    release: "rel-paper-harbour",
    track_number: "3",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-pier-lights",
    title: "Pier Lights",
    mix_name: "Acoustic",
    isrc: "GBTRK2500063",
    release: "rel-paper-harbour",
    track_number: "4",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-fog-signal",
    title: "Fog Signal",
    mix_name: "Original Mix",
    isrc: "GBTRK2500064",
    release: "rel-paper-harbour",
    track_number: "5",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-rope-and-anchor",
    title: "Rope & Anchor",
    mix_name: "Extended Mix",
    isrc: "GBTRK2500065",
    release: "rel-paper-harbour",
    track_number: "6",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-gull-song",
    title: "Gull Song",
    mix_name: "Original Mix",
    isrc: "GBTRK2500066",
    release: "rel-paper-harbour",
    track_number: "7",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-ferry-at-dusk",
    title: "Ferry at Dusk",
    mix_name: "Instrumental",
    isrc: "GBTRK2500067",
    release: "rel-paper-harbour",
    track_number: "8",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-breakwater",
    title: "Breakwater",
    mix_name: "Original Mix",
    isrc: "GBTRK2500068",
    release: "rel-paper-harbour",
    track_number: "9",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-lighthouse-keeper",
    title: "Lighthouse Keeper",
    mix_name: "Radio Edit",
    isrc: "GBTRK2500069",
    release: "rel-paper-harbour",
    track_number: "10",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-paper-boats",
    title: "Paper Boats",
    mix_name: "Acoustic",
    isrc: "GBTRK2500070",
    release: "rel-paper-harbour",
    track_number: "11",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-last-tide",
    title: "Last Tide",
    mix_name: "Reprise",
    isrc: "GBTRK2500071",
    release: "rel-paper-harbour",
    track_number: "12",
    artist: "The Hollow Pines",
  },
  // Paper Harbour, on: two tracks on each of the newer blocking cases' demo contracts.
  {
    id: "rec-north-pier",
    title: "North Pier",
    mix_name: "Original Mix",
    isrc: "GBTRK2500072",
    release: "rel-paper-harbour",
    track_number: "13",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-tidewater",
    title: "Tidewater",
    mix_name: "Radio Edit",
    isrc: "GBTRK2500073",
    release: "rel-paper-harbour",
    track_number: "14",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-moorings",
    title: "Moorings",
    mix_name: "Original Mix",
    isrc: "GBTRK2500074",
    release: "rel-paper-harbour",
    track_number: "15",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-harbour-bell",
    title: "Harbour Bell",
    mix_name: "Acoustic",
    isrc: "GBTRK2500075",
    release: "rel-paper-harbour",
    track_number: "16",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-sea-glass",
    title: "Sea Glass",
    mix_name: "Original Mix",
    isrc: "GBTRK2500076",
    release: "rel-paper-harbour",
    track_number: "17",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-quayside",
    title: "Quayside",
    mix_name: "Extended Mix",
    isrc: "GBTRK2500077",
    release: "rel-paper-harbour",
    track_number: "18",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-lantern-row",
    title: "Lantern Row",
    mix_name: "Original Mix",
    isrc: "GBTRK2500078",
    release: "rel-paper-harbour",
    track_number: "19",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-slipway",
    title: "Slipway",
    mix_name: "Demo",
    isrc: "GBTRK2500079",
    release: "rel-paper-harbour",
    track_number: "20",
    artist: "The Hollow Pines",
  },
];

/** The releases a track is on, with its number on each: the one it came out on, then any others (a compilation). */
export function releasesOf(
  recording: (typeof RECORDINGS)[number],
): { release: string; track_number: string }[] {
  return [
    { release: recording.release, track_number: recording.track_number },
    ...(("also_on" in recording && recording.also_on) || []),
  ];
}

/** The releases a track is on: the one it came out on, then any others (a compilation). */
export function releaseIdsOf(recording: (typeof RECORDINGS)[number]): string[] {
  return releasesOf(recording).map((r) => r.release);
}

/** The tracks on a release, the ones also on it included. */
export function recordingsOn(releaseId: string) {
  return RECORDINGS.filter((r) => releaseIdsOf(r).includes(releaseId));
}

/** Contracts already on the label — what a sub-contract is made under. */
export const CONTRACTS = [
  // Every tab filled in, to see them all: three payees (one with a long name), opening
  // balances, advances, a contract PDF, and ten tracks — two split their own way — with
  // format and expense type exceptions (CONTRACT_EXTRAS); no sub-contract, for its empty state.
  {
    id: "contract-showcase",
    name: "The Hollow Pines & June Ashworth — Showcase Deal",
    contract_type: "recording",
    status: "active",
    start_date: "2025-03-01",
    end_date: "2029-02-28",
    label_share_percent: "50.00",
    advance_amount: "7500.00",
    file_name: "Showcase Deal — signed.pdf",
    payees: [
      { payee: "payee-hollow-pines", share: 60, opening_balance: "1250.00" },
      { payee: "payee-june-ashworth", share: 25, opening_balance: "-300.00" },
      { payee: "payee-aurelia-montgomery", share: 15 },
    ],
    tracks: [
      "rec-copper-sky",
      "rec-quiet-engines",
      "rec-dust-and-chrome",
      "rec-signal-fire",
      "rec-long-way-home",
      "rec-northern-lines",
      "rec-paper-planes",
      "rec-cold-light",
      "rec-harbour-wall",
      "rec-last-ferry",
    ],
  },
  // For the linking flow: two payees 50/50, the label 50%, two of Tidal Rooms' tracks —
  // a track linked later takes the same split.
  {
    id: "contract-tidal-rooms",
    name: "Marcus Vale & June Ashworth — Tidal Rooms Deal",
    contract_type: "recording",
    status: "active",
    start_date: "2026-06-01",
    end_date: "2029-05-31",
    label_share_percent: "50.00",
    payees: [
      { payee: "payee-marcus-vale", share: 50 },
      { payee: "payee-june-ashworth", share: 50 },
    ],
    tracks: ["rec-tidal-rooms", "rec-lantern-bay"],
  }, // Demo contract 1: eight tracks, all on the same split — the label 50%, Nova Skye and
  // Marcus Vale 50/50 — with no exceptions by format, track or expense type.
  {
    id: "demo-contract-1",
    name: "Demo contract 1",
    contract_type: "recording",
    status: "active",
    start_date: "2026-09-01",
    end_date: "2029-08-31",
    file_name: "Demo contract 1 — signed.pdf",
    label_share_percent: "50.00",
    payees: [
      { payee: "payee-nova-skye", share: 50 },
      { payee: "payee-marcus-vale", share: 50 },
    ],
    tracks: [
      "rec-moonlit-path",
      "rec-fern-hollow",
      "rec-glasshouse",
      "rec-night-bloom",
      "rec-quiet-orchard",
      "rec-lantern-moth",
      "rec-ivy-steps",
      "rec-dawn-chorus",
    ],
  },
  // Ten payees, to see how lists and tables take many: the label 50%, the payees 10% each
  // of the rest, on the four Neon Harbour tracks.
  {
    id: "demo-contract-ten-payees",
    name: "Demo contract – 10 payees",
    contract_type: "recording",
    status: "active",
    start_date: "2026-10-01",
    end_date: "2029-09-30",
    label_share_percent: "50.00",
    payees: [
      "payee-nova-skye",
      "payee-marcus-vale",
      "payee-hollow-pines",
      "payee-june-ashworth",
      "payee-aurelia-montgomery",
      "payee-elio-marchetti",
      "payee-saffron-reed",
      "payee-kai-nakamura",
      "payee-lumen-collective",
      "payee-odessa-grey",
    ].map((payee) => ({ payee, share: 10 })),
    tracks: ["rec-midnight-signal", "rec-harbour-lights", "rec-glass-avenue", "rec-undertow"],
  },
  // Edit mode's blocking cases. Has revenue: locked as the app does (can_delete false) — payees, splits and the PDF; its tracks can't be unlinked (proposed).
  {
    id: "demo-contract-revenue",
    name: "Demo contract – has revenue",
    contract_type: "recording",
    status: "active",
    start_date: "2025-10-01",
    end_date: "2028-09-30",
    file_name: "Demo contract – has revenue — signed.pdf",
    label_share_percent: "50.00",
    advance_amount: "6000.00",
    payees: [
      { payee: "payee-hollow-pines", share: 50, opening_balance: "1500.00" },
      { payee: "payee-june-ashworth", share: 50, opening_balance: "-250.00" },
    ],
    tracks: ["rec-harbour-lamps", "rec-salt-wind", "rec-low-tide-waltz", "rec-pier-lights"],
    blocking: "revenue",
  },
  // In a draft royalty statement: nothing locked, as the app today (drafts regenerate when sent) — a note only.
  {
    id: "demo-contract-draft-statement",
    name: "Demo contract – in a draft statement",
    contract_type: "recording",
    status: "active",
    start_date: "2025-10-01",
    end_date: "2028-09-30",
    file_name: "Demo contract – in a draft statement — signed.pdf",
    label_share_percent: "50.00",
    advance_amount: "3500.00",
    payees: [
      { payee: "payee-hollow-pines", share: 50, opening_balance: "800.00" },
      { payee: "payee-june-ashworth", share: 50, opening_balance: "0.00" },
    ],
    tracks: ["rec-fog-signal", "rec-rope-and-anchor", "rec-gull-song", "rec-ferry-at-dusk"],
    blocking: "draft-statement",
  },
  // In a sent royalty statement: locked as the app does (can_delete false) — payees, splits and the PDF; its tracks can't be unlinked (proposed).
  {
    id: "demo-contract-sent-statement",
    name: "Demo contract – in a sent statement",
    contract_type: "recording",
    status: "active",
    start_date: "2025-10-01",
    end_date: "2028-09-30",
    file_name: "Demo contract – in a sent statement — signed.pdf",
    label_share_percent: "50.00",
    advance_amount: "5000.00",
    payees: [
      { payee: "payee-hollow-pines", share: 50, opening_balance: "-400.00" },
      { payee: "payee-june-ashworth", share: 50, opening_balance: "1200.00" },
    ],
    tracks: ["rec-breakwater", "rec-lighthouse-keeper", "rec-paper-boats", "rec-last-tide"],
    blocking: "sent-statement",
  },
  // Has expenses, no revenue: locked as revenue is (can_delete false) — the app's "revenue/expenses".
  {
    id: "demo-contract-expenses",
    name: "Demo contract – has expenses",
    contract_type: "recording",
    status: "active",
    start_date: "2025-10-01",
    end_date: "2028-09-30",
    file_name: "Demo contract – has expenses — signed.pdf",
    label_share_percent: "50.00",
    advance_amount: "4000.00",
    payees: [
      { payee: "payee-hollow-pines", share: 50, opening_balance: "600.00" },
      { payee: "payee-june-ashworth", share: 50, opening_balance: "0.00" },
    ],
    tracks: ["rec-north-pier", "rec-tidewater"],
    blocking: "expenses",
  },
  // In a paid royalty statement: locked as a sent one is (can_delete false).
  {
    id: "demo-contract-paid-statement",
    name: "Demo contract – in a paid statement",
    contract_type: "recording",
    status: "active",
    start_date: "2025-10-01",
    end_date: "2028-09-30",
    file_name: "Demo contract – in a paid statement — signed.pdf",
    label_share_percent: "50.00",
    advance_amount: "4500.00",
    payees: [
      { payee: "payee-hollow-pines", share: 50, opening_balance: "-200.00" },
      { payee: "payee-june-ashworth", share: 50, opening_balance: "900.00" },
    ],
    tracks: ["rec-moorings", "rec-harbour-bell"],
    blocking: "paid-statement",
  },
  // Journal entries, nothing else yet: as the app's rates_locked alone — only the rates (label shares) locked; payees, PDF and currency still editable.
  {
    id: "demo-contract-journal-entries",
    name: "Demo contract – has journal entries",
    contract_type: "recording",
    status: "active",
    start_date: "2025-10-01",
    end_date: "2028-09-30",
    file_name: "Demo contract – has journal entries — signed.pdf",
    label_share_percent: "50.00",
    advance_amount: "3000.00",
    payees: [
      { payee: "payee-hollow-pines", share: 50, opening_balance: "300.00" },
      { payee: "payee-june-ashworth", share: 50, opening_balance: "-150.00" },
    ],
    tracks: ["rec-sea-glass", "rec-quayside"],
    blocking: "journal-entries",
  },
];

/**
 * What the sample contracts hold beyond the API's fields, as the builder keeps it.
 */
export const CONTRACT_EXTRAS: Record<string, ContractExtras> = {
  // The blocking cases' demo contracts: opening balances (on their payees) and advances.
  "demo-contract-revenue": {
    formatRules: [],
    advances: {
      "payee-hollow-pines": { amount: "4000", recoupable: "100", paidOn: new Date("2025-10-15") },
      "payee-june-ashworth": { amount: "2000", recoupable: "50", paidOn: new Date("2025-11-01") },
    },
  },
  "demo-contract-draft-statement": {
    formatRules: [],
    advances: {
      "payee-hollow-pines": { amount: "2500", recoupable: "100", paidOn: new Date("2025-10-20") },
      "payee-june-ashworth": { amount: "1000", recoupable: "100", paidOn: null },
    },
  },
  "demo-contract-expenses": {
    formatRules: [],
    advances: {
      "payee-hollow-pines": { amount: "2500", recoupable: "100", paidOn: new Date("2025-10-10") },
      "payee-june-ashworth": { amount: "1500", recoupable: "100", paidOn: null },
    },
  },
  "demo-contract-paid-statement": {
    formatRules: [],
    advances: {
      "payee-hollow-pines": { amount: "3000", recoupable: "100", paidOn: new Date("2025-10-10") },
      "payee-june-ashworth": { amount: "1500", recoupable: "100", paidOn: null },
    },
  },
  "demo-contract-journal-entries": {
    formatRules: [],
    advances: {
      "payee-hollow-pines": { amount: "2000", recoupable: "100", paidOn: new Date("2025-10-10") },
      "payee-june-ashworth": { amount: "1000", recoupable: "100", paidOn: null },
    },
  },
  "demo-contract-sent-statement": {
    formatRules: [],
    advances: {
      "payee-hollow-pines": { amount: "3000", recoupable: "75", paidOn: new Date("2025-10-05") },
      "payee-june-ashworth": { amount: "2000", recoupable: "100", paidOn: new Date("2025-10-05") },
    },
  },
  "contract-showcase": {
    formatRules: [],
    // 50/50 — except vinyl (the label 70%) and CDs (60%).
    poolRows: [
      { format: "all", label: "50" },
      { format: "vinyl", label: "70" },
      { format: "cd", label: "60" },
    ],
    // The artist pool by track: Signal Fire and Cold Light their own way, the rest as the
    // payees' Revenue split (60/25/15).
    artistShares: {
      "rec-signal-fire": {
        "payee-hollow-pines": "40",
        "payee-june-ashworth": "40",
        "payee-aurelia-montgomery": "20",
      },
      "rec-cold-light": {
        "payee-hollow-pines": "50",
        "payee-june-ashworth": "50",
        "payee-aurelia-montgomery": "0",
      },
    },
    // Expenses 50/50 — except mastering (the label 80%) and promotion (30%); mixing as all
    // expenses, so on their row.
    expenseRows: [
      { type: "all", label: "50" },
      { type: "mastering", label: "80" },
      { type: "promotion", label: "30" },
      { type: "mixing", label: "50" },
    ],
    advances: {
      "payee-hollow-pines": { amount: "5000", recoupable: "100", paidOn: new Date("2025-03-15") },
      "payee-june-ashworth": { amount: "2500", recoupable: "50", paidOn: null },
    },
  },
};

/**
 * The sample contracts' sub-contracts: none — the showcase's Sub-contracts tab shows its
 * empty state. One saved in the sub-contract builder is kept in the session (store.ts).
 */
export const SUB_CONTRACTS: SavedSubContract[] = [];

/**
 * What "processing" an uploaded contract PDF finds, whatever the file: the
 * scenario card's deal. The label and Nova Skye split 50/50, except vinyl
 * (label 70%, Nova Skye 30%). Nova Skye is advanced $5,000, half of it
 * recoupable, and the contract covers the 4 tracks of Afterglow — on no other
 * contract yet. Expenses
 * are split as revenue. The contract's name is the file's.
 */
export const EXTRACTED_CONTRACT = {
  fields: {
    contract_type: "recording",
    status: "draft",
    start_date: "2026-01-01",
    end_date: null,
    territory: "worldwide",
    currency: "USD",
    statement_period: "half_yearly",
    payment_threshold: "50.00",
    label_share_percent: "50.00",
    advance_amount: "5000.00",
    // As the API keeps it: each payee's part of what's left after the label.
    payees: [{ payee: "payee-nova-skye", share_percentage: 100 }],
  },
  tracks: ["rec-static-bloom", "rec-low-orbit", "rec-coastline", "rec-after-hours"],
  extras: {
    formatRules: [
      {
        key: "rule-extracted-vinyl",
        format: "vinyl",
        labelShare: "70",
        shares: { "payee-nova-skye": "30" },
      },
    ],
    advances: { "payee-nova-skye": { amount: "5000", recoupable: "50", paidOn: null } },
    advancePayees: [{ id: "payee-nova-skye", name: "Nova Skye" }],
    expenseSplit: { labelShare: "50", shares: { "payee-nova-skye": "50" } },
  } satisfies ContractExtras,
};
